import { Flex } from '@atlaskit/primitives';
import JiraIssueLabel from '@databases/content-link/JiraIssueLabel';
import JiraIssueLink from '@databases/content-link/JiraIssueLink';
import { type JiraIssueRef } from '@databases/types/confluence';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import {
  MultiValueChangeType,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { isNonEmptyJiraIssueValue } from '@databases/utils/database/field-types/jiraIssue';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';

import { FieldValueRemoveButton, filterOutRemovedValue } from '../popover-edit-common';

import { type ResolvedValue, type Value } from './types';

interface JiraIssuePopoverValueProps {
  onChange: (value: Value[], change: ValueChange | null) => void;
  resolvedValue: ResolvedValue;
  resolvedValues: ResolvedValue[];
  isLinkPreview?: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  autoFocus: boolean | undefined;
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE>;
}

export const JiraIssuePopoverValue: FunctionComponent<JiraIssuePopoverValueProps> = ({
  resolvedValue,
  resolvedValues,
  autoFocus,
  onChange,
  isLinkPreview,
  addDataSectionRef,
  field,
}) => {
  const jiraIssueRef = useMemo(
    () =>
      isNonEmptyJiraIssueValue(resolvedValue)
        ? {
            _id: resolvedValue._id,
            key: resolvedValue.key,
            cloudId: field.settings.cloudId,
            serverId: field.settings.jiraServerId,
          }
        : null,
    [resolvedValue, field.settings.cloudId, field.settings.jiraServerId]
  );

  const jiraIssueValueIndex = resolvedValues.findIndex(
    (value) =>
      value && 'key' in value && 'key' in resolvedValue && value.key === resolvedValue.key
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: resolvedValues,
    indexToRemove: jiraIssueValueIndex,
  });

  const onItemDelete = useCallback(() => {
    onChange(remainingValues, {
      type: DatabaseFieldType.JIRA_ISSUE,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: jiraIssueValueIndex,
      },
    });
    // focus the select input after each item removal
    addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
  }, [addDataSectionRef, jiraIssueValueIndex, onChange, remainingValues]);

  return (
    <Flex
      testId={'jira-issue-value'}
      justifyContent={'start'}
      alignItems={'center'}
      gap={'space.075'}
    >
      {isLinkPreview ? (
        <JiraIssueLink issue={jiraIssueRef as JiraIssueRef} useSmartLink={false} />
      ) : (
        <JiraIssueLabel issue={jiraIssueRef as JiraIssueRef} />
      )}
      <FieldValueRemoveButton
        testId={'jira-issue-remove-button'}
        onItemDelete={onItemDelete}
        autoFocus={autoFocus}
      />
    </Flex>
  );
};
